import React, { type PropsWithChildren } from 'react';
import { StyleSheet } from 'react-native';

import { theme } from '@garnish/constants';
import { LoadingPlaceholder, useResponsive } from '@garnish/core';

import { StripeElementsProvider } from '@order/integrations-stripe';

import { NotFoundView } from '../../../../../../components/NotFoundView';
import { type GiftCardCheckoutFormReturn } from '../../../../form';
import { useCustomerPaymentInfoQuery } from '../../../../graphql/CustomerPaymentInfo.generated';
import { GiftCardCheckoutFormContainer } from '../../components/GiftCardCheckoutFormContainer';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardCheckoutPaymentProvider = (props: GiftCardCheckoutPaymentProviderProps) => {
  const { form, children } = props;

  const { match } = useResponsive();
  const loadingContainerStyles = [
    styles.loadingContainer,
    match([null, styles.loadingContainerSM]),
  ];

  const [{ data, error, fetching }] = useCustomerPaymentInfoQuery({
    requestPolicy: 'network-only',
  });

  const amount = form.watch('amount');
  const stripeCustomerId = data?.customerPaymentInfo?.id;
  const stripeCustomerKey = data?.customerPaymentInfo?.paymentEphemeralKey;
  const stripeSession = data?.customerPaymentInfo?.paymentSessionId;

  const hasStripeKeys = stripeCustomerId && stripeCustomerKey && stripeSession;

  if (fetching) {
    return (
      <GiftCardCheckoutFormContainer style={loadingContainerStyles}>
        <LoadingPlaceholder rows={6} rowHeight={[160, 400, 160, 400, 400, 160]} palette="cream" />
      </GiftCardCheckoutFormContainer>
    );
  }

  if (error || !hasStripeKeys) {
    return <NotFoundView />;
  }

  return (
    <StripeElementsProvider source="gift-card" amount={amount} sessionId={stripeSession}>
      {children as React.ReactElement}
    </StripeElementsProvider>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  loadingContainer: {
    backgroundColor: theme.colors.APP_BACKGROUND,
    padding: theme.spacing['4'],
  },
  loadingContainerSM: {
    width: '100%',
    maxWidth: 600,
    alignSelf: 'center',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardCheckoutPaymentProviderProps = PropsWithChildren & {
  form: GiftCardCheckoutFormReturn;
};
